import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  CancelOutlined as NotEligibleIcon,
  SyncOutlined as SyncIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../context/AuthContext';
import { isGroupScopeRole } from '../../../constants/csAdminRoles';
import { csAdminApi } from '../../../services/api';
import { mapDirectorPoolEntryFromApi } from '../../../utils/apiMappers';

const SearchPoolStep = ({ selectedDirectorId, onSelectDirector, excludeCompanyId, companyName, errors }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [directors, setDirectors] = useState([]);
  const [filteredDirectors, setFilteredDirectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const canSyncPool = isGroupScopeRole(user?.role);

  useEffect(() => {
    fetchPoolDirectors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeCompanyId]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredDirectors(directors);
      return;
    }
    const query = searchQuery.toLowerCase();
    setFilteredDirectors(
      directors.filter((director) =>
        director.fullName.toLowerCase().includes(query)
        || director.emiratesId?.toLowerCase().includes(query)
        || director.position?.toLowerCase().includes(query)
        || director.currentCompany?.toLowerCase().includes(query)
      )
    );
  }, [searchQuery, directors]);

  const fetchPoolDirectors = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await csAdminApi.getDirectors({
        pool_for_company_id: excludeCompanyId,
      });
      const list = (response.data?.data || [])
        .map((row) => mapDirectorPoolEntryFromApi(row, excludeCompanyId))
        .filter(Boolean)
        .sort((a, b) => a.fullName.localeCompare(b.fullName));
      setDirectors(list);
      setFilteredDirectors(list);
    } catch (err) {
      console.error('Error fetching director pool:', err);
      setError('Failed to load director pool');
      setDirectors([]);
      setFilteredDirectors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPool = async () => {
        try {
      setSyncing(true);
      setError('');
      const linksRes = await csAdminApi.backfillPersonRegistry();
      const links = linksRes.data?.data || {};
      await csAdminApi.backfillPersonDocuments();
      enqueueSnackbar(
        `Pool synced — ${links.linked ?? 0} director(s) linked`,
        { variant: 'success' }
      );
      await fetchPoolDirectors();
    } catch (err) {
      console.error('Pool sync failed:', err);
      setError(err.response?.data?.error?.message || 'Failed to sync director pool');
    } finally {
      setSyncing(false);
    }
  };

  const getDirectorKey = (director) => String(
    director.personId ?? director.person_id ?? director.id
  );

  const handleSelectDirector = (director) => {
    if (!director.isEligible) {
      setError(director.ineligibilityReason || 'This director is not eligible');
      return;
    }
    const key = getDirectorKey(director);
    if (String(selectedDirectorId) === key || String(selectedDirectorId) === String(director.id)) {
      onSelectDirector(null);
      setError('');
      return;
    }
    onSelectDirector({ ...director, id: director.id, personId: director.personId ?? director.person_id });
    setError('');
  };

  const selectedDirector = directors.find(
    (d) => getDirectorKey(d) === String(selectedDirectorId)
      || String(d.id) === String(selectedDirectorId)
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Search Director Pool
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Select an existing director to add to <strong>{companyName}</strong>
      </Typography>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Adding director to:
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          {companyName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Directors already in this company are excluded from the pool
        </Typography>
      </Box>

      {errors.selectedDirectorId && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.selectedDirectorId}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search by name, Emirates ID, position, or company..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading directors...
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Available Directors ({filteredDirectors.length}):
          </Typography>
          <List
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 350,
              overflow: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            {filteredDirectors.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No directors found"
                  secondary={
                    canSyncPool
                      ? 'Run Sync Pool to import existing Phase 1 directors, or add a new director'
                      : 'Try adjusting your search or add a new director'
                  }
                  sx={{ textAlign: 'center', py: 2 }}
                />
              </ListItem>
            ) : (
              filteredDirectors.map((director) => {
                const directorKey = getDirectorKey(director);
                const isSelected = String(selectedDirectorId) === directorKey
                  || String(selectedDirectorId) === String(director.id);
                return (
                <ListItem
                  key={directorKey}
                  button
                  selected={isSelected}
                  onClick={() => handleSelectDirector(director)}
                  disabled={!director.isEligible}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                    opacity: director.isEligible ? 1 : 0.6,
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isSelected}
                      disabled={!director.isEligible}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleSelectDirector(director)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body1" fontWeight={500}>
                          {director.fullName}
                        </Typography>
                        {!director.isEligible && (
                          <Chip
                            label="Not Eligible"
                            size="small"
                            color="error"
                            icon={<NotEligibleIcon />}
                          />
                        )}
                      </Box>
                    )}
                    secondary={(
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {director.position} • Current: {director.currentCompany}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Emirates ID: {director.emiratesId}
                        </Typography>
                        {!director.isEligible && director.ineligibilityReason && (
                          <Typography variant="caption" color="error.main">
                            {director.ineligibilityReason}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                </ListItem>
                );
              })
            )}
          </List>

          {canSyncPool && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                onClick={handleSyncPool}
                disabled={syncing || loading}
              >
                Sync Pool from Phase 1
              </Button>
            </Box>
          )}
        </>
      )}

      {selectedDirector && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
          <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 0.5 }}>
            Selected Director
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {selectedDirector.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedDirector.position} • Emirates ID: {selectedDirector.emiratesId}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Currently at: {selectedDirector.currentCompany}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SearchPoolStep;
